import "./SavedForecasts.scss";
import cn from "classnames";
import * as R from "ramda";
import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { MdAdd, MdContentCopy, MdDelete, MdMoreVert, MdRemoveRedEye } from "react-icons/md";
import { Forecast, ForecastGroup } from "@blisspointmedia/bpm-types/dist/Forecasting";
import { navigate, RouteComponentProps } from "@reach/router";
import {
  BPMTable,
  Button,
  ButtonFrameworkVariant,
  ButtonType,
  FilterBar,
  FullPageSpinner,
  TextToggleButton,
  ToggleSwitch,
  TypedHeader
} from "../../Components";
import TableWidget from "../../Components/TableWidget/TableWidget";
import { SetError } from "../../redux/modals";
import { dateToMddyy } from "../../utils/date-utils";
import { BudgetViewOptions, formatRawBudget, PageTab } from "../forecastingConstants";
import { isForecastLastInGroup } from "../forecastingUtils";
import StatusChip from "./StatusChip";

interface SavedForecastsProps {
  forecastGroups: ForecastGroup[] | undefined;
  budgetView: BudgetViewOptions;
  setBudgetView: React.Dispatch<React.SetStateAction<BudgetViewOptions>>;
  setError: SetError;
  isAdmin: boolean;
  showWIPFeatures?: boolean;
}
type ForecastRow = Omit<ForecastGroup, "forecasts"> & Forecast;

const BASE_ROW_HEIGHT = 50;
const SM_COL_WIDTH = 100;
const MED_COL_WIDTH = 150;
const LG_COL_WIDTH = 200;
const OUTER_X_PADDING = 16;
const INNER_X_PADDING = 24;

const SavedForecasts = ({
  forecastGroups,
  budgetView,
  setBudgetView,
  setError,
  isAdmin,
  showWIPFeatures = false,
}: RouteComponentProps & SavedForecastsProps): JSX.Element => {
  const doubleTableRef = useRef<HTMLDivElement>(null);
  const [doubleTableWidth, setDoubleTableWidth] = useState<number | null>(null);
  const [leftTableWidth, setLeftTableWidth] = useState<number | null>(null);
  const [resizeActive, setResizeActive] = useState(false);

  // initial measurement of doubleTableWidth, ensuring DOM is ready
  useLayoutEffect(() => {
    const measureWidth = () => {
      if (doubleTableRef.current) {
        setDoubleTableWidth(doubleTableRef.current.offsetWidth);
      }
    };

    measureWidth();
  }, [doubleTableRef]);

  // resize listener of doubleTableWidth, side effect doesn't block painting
  useEffect(() => {
    const measureWidthOnResize = () => {
      if (doubleTableRef.current) {
        setDoubleTableWidth(doubleTableRef.current.offsetWidth);
      }
    };

    window.addEventListener("resize", measureWidthOnResize);
    return () => {
      window.removeEventListener("resize", measureWidthOnResize);
    };
  }, [doubleTableRef]);

  // Initial size of the left table should be 35% of the container, but at least 700px
  useEffect(() => {
    if (doubleTableWidth) {
      setLeftTableWidth(Math.max(doubleTableWidth * 0.35, 700));
    }
  }, [doubleTableWidth]);

  const rightTableWidth = useMemo(() => {
    return (doubleTableWidth ?? 0) - (leftTableWidth ?? 0);
  }, [doubleTableWidth, leftTableWidth]);

  // Left table's Controls column will grow to fill additional space,
  // but maintain a minimum width of LG_COL_WIDTH
  const leftTableGrowingColWidth = useMemo(() => {
    return Math.max((leftTableWidth ?? 0) - MED_COL_WIDTH * 3 - OUTER_X_PADDING, LG_COL_WIDTH);
  }, [leftTableWidth]);

  // Comparison Budgets and Last Modified columns will both grow to fill additional space,
  // but maintain a minimum width of LG_COL_WIDTH
  const rightTableGrowingColWidth = useMemo(() => {
    return Math.max(
      (rightTableWidth - SM_COL_WIDTH * 2 - MED_COL_WIDTH * 2 - OUTER_X_PADDING - INNER_X_PADDING) /
        2,
      LG_COL_WIDTH
    );
  }, [rightTableWidth]);

  const rightData: ForecastRow[] = useMemo(() => {
    if (!forecastGroups) {
      return [];
    }

    return R.flatten(
      forecastGroups.map(({ forecasts, ...remainingForecastVals }) =>
        forecasts.map(forecast => ({ ...forecast, ...remainingForecastVals }))
      )
    );
  }, [forecastGroups]);

  const leftHeaders: TypedHeader<ForecastGroup>[] = useMemo(() => {
    return [
      {
        label: "Forecast Dates",
        name: "dateRange",
        renderer: ({ startDate, endDate }) => (
          <div className="savedForecastsDateRange">
            {dateToMddyy(startDate)}–{dateToMddyy(endDate)}
          </div>
        ),
        nonInteractive: true,
        width: MED_COL_WIDTH + OUTER_X_PADDING,
      },
      {
        label: "KPI Volume",
        name: "volumeKPIs",
        renderer: ({ kpis }) => (
          <div className="savedForecastsVolumeKPIs">
            {kpis
              .filter(kpi => kpi.type === "Volume")
              .map(kpi => kpi.name)
              .join(", ")}
          </div>
        ),
        nonInteractive: true,
        width: MED_COL_WIDTH,
      },
      {
        label: "Revenue KPI",
        name: "revenueKPIs",

        renderer: ({ kpis }) => (
          <div className="savedForecastsRevenueKPIs">
            {kpis
              .filter(kpi => kpi.type === "Revenue")
              .map(kpi => kpi.name)
              .join(", ")}
          </div>
        ),
        nonInteractive: true,
        width: MED_COL_WIDTH,
      },
      {
        label: "",
        name: "controls",
        renderer: row => (
          <div className="savedForecastsControls">
            <div className="savedForecastsControlsLeft">
              <StatusChip status={row.status} />
            </div>
            {showWIPFeatures && (
              <div className="savedForecastsControlsRight">
                <Button
                  icon={<MdContentCopy />}
                  type={ButtonType.EMPTY}
                  variant={ButtonFrameworkVariant.ICON_ONLY}
                  design="secondary"
                  onClick={() => {
                    console.info("TODO: Duplicate row", row);
                    setError({ message: "Duplicate not yet impemented" });
                  }}
                />
                <Button
                  icon={<MdDelete />}
                  type={ButtonType.EMPTY}
                  variant={ButtonFrameworkVariant.ICON_ONLY}
                  design="secondary"
                  onClick={() => {
                    console.info("TODO: Delete row", row);
                    setError({ message: "Delete not yet impemented" });
                  }}
                />
              </div>
            )}
          </div>
        ),
        nonInteractive: true,
        width: leftTableGrowingColWidth,
      },
    ];
  }, [leftTableGrowingColWidth, setError, showWIPFeatures]);

  const rightHeaders: TypedHeader<ForecastRow>[] = useMemo(() => {
    return [
      {
        label: "Channel",
        name: "channel",
        renderer: ({ channel, id, groupId }) => (
          <div
            className={cn("savedForecastsChannel", {
              lastInGroup: isForecastLastInGroup({ forecastGroups, groupId, id }),
            })}
          >
            {channel}
          </div>
        ),
        nonInteractive: true,
        width: SM_COL_WIDTH + INNER_X_PADDING,
      },
      {
        label: "Platform",
        name: "platform",
        renderer: ({ platform, groupId, id }) => (
          <div
            className={cn("savedForecastsPlatform", {
              lastInGroup: isForecastLastInGroup({ forecastGroups, groupId, id }),
            })}
          >
            {platform ?? ""}
          </div>
        ),
        nonInteractive: true,
        width: SM_COL_WIDTH,
      },
      {
        label: "Primary Budget",
        name: "primaryBudget",
        renderer: ({ primaryBudget, groupId, id }) => (
          <div
            className={cn("savedForecastsPrimaryBudget", {
              lastInGroup: isForecastLastInGroup({ forecastGroups, groupId, id }),
            })}
          >
            {formatRawBudget(primaryBudget)}
          </div>
        ),
        nonInteractive: true,
        width: MED_COL_WIDTH,
      },
      {
        label: "Comparison Budgets",
        name: "comparisonBudgets",
        renderer: ({ comparisonBudgets, groupId, id }) => (
          <div
            className={cn("savedForecastsComparisonBudgets", {
              lastInGroup: isForecastLastInGroup({ forecastGroups, groupId, id }),
            })}
          >
            {comparisonBudgets.map(budget => formatRawBudget(budget)).join(", ")}
          </div>
        ),
        nonInteractive: true,
        width: rightTableGrowingColWidth,
      },
      {
        label: "Last Modified",
        name: "lastModified",
        renderer: ({ lastModifiedDate, lastModifiedUser, groupId, id }) => {
          return (
            <div
              className={cn("savedForecastsLastModified", {
                lastInGroup: isForecastLastInGroup({ forecastGroups, groupId, id }),
              })}
            >
              By {lastModifiedUser.fullName} on {lastModifiedDate}
            </div>
          );
        },
        nonInteractive: true,
        width: rightTableGrowingColWidth,
      },
      {
        label: "Client Facing",
        name: "clientFacing",
        renderer: ({ isClientFacing, groupId, id, status }) => (
          <div
            className={cn("savedForecastsControls", {
              lastInGroup: isForecastLastInGroup({ forecastGroups, groupId, id }),
            })}
          >
            {showWIPFeatures && (
              <div className="savedForecastsControlsLeft">
                <ToggleSwitch
                  label=""
                  checked={isClientFacing}
                  onChange={() =>
                    setError({ message: "Updating client facing not yet supported." })
                  }
                  labelPosition="none"
                  disabled={status !== "Completed"}
                />
              </div>
            )}
            <div className="savedForecastsControlsRight">
              {status === "Completed" && (
                <Button
                  type={ButtonType.EMPTY}
                  variant={ButtonFrameworkVariant.ICON_ONLY}
                  icon={<MdRemoveRedEye />}
                  onClick={() => {
                    navigate(`${PageTab.FORECAST_RESULTS}/${id}`);
                  }}
                />
              )}
              {showWIPFeatures && (
                <Button
                  type={ButtonType.EMPTY}
                  variant={ButtonFrameworkVariant.ICON_ONLY}
                  design="secondary"
                  icon={<MdMoreVert />}
                  onClick={() =>
                    setError({
                      message: "TODO: Display more options menu",
                    })
                  }
                />
              )}
            </div>
          </div>
        ),
        nonInteractive: true,
        width: MED_COL_WIDTH + OUTER_X_PADDING,
      },
    ];
  }, [forecastGroups, rightTableGrowingColWidth, setError, showWIPFeatures]);

  const resizeFrame: React.MouseEventHandler<HTMLDivElement> = e => {
    if (resizeActive) {
      const leftNavWidth = 60;
      const adjustedWidth = e.clientX - leftNavWidth - 28;
      setLeftTableWidth(adjustedWidth);
    }
  };

  if (forecastGroups && forecastGroups.length === 0) {
    // [DEV-18749] TODO: Pretty empty state that lets people request their first forecast
    return <div>Placeholder Empty State</div>;
  }

  return (
    <div className="savedForecastsContainer" onMouseUp={() => setResizeActive(false)}>
      <TableWidget
        twHeaderTopLeft={
          showWIPFeatures ? (
            <FilterBar
              lines={[]}
              onFilter={() => console.error("TODO: setFilter")}
              options={[]}
              hasAdvanced
            />
          ) : undefined
        }
        twHeaderTopRight={
          <>
            {showWIPFeatures && (
              <TextToggleButton
                className="forecastsBudgetDisplayToggle"
                design="secondary-light"
                options={[
                  { key: "showPercentage", label: "Show % Budgets" },
                  { key: "showDollars", label: "Show $ Budgets" },
                ]}
                selectedOption={budgetView}
                onChange={option => setBudgetView(option)}
              />
            )}
            {showWIPFeatures && isAdmin && (
              <Button
                type={ButtonType.FILLED}
                variant={ButtonFrameworkVariant.LEADING_ICON}
                icon={<MdAdd />}
                onClick={() => {
                  // [DEV-18594] TODO
                  setError({ message: "Requesting new forecasts not yet supported" });
                }}
              >
                New Forecast
              </Button>
            )}
          </>
        }
        tableComponent={
          <div className="savedForecastsDoubleTable" ref={doubleTableRef}>
            {forecastGroups ? (
              <>
                <div
                  className="savedForecastsTableContainer leftTableContainer"
                  style={{ width: leftTableWidth ?? "auto" }}
                >
                  <BPMTable
                    data={forecastGroups}
                    headers={leftHeaders}
                    headersRenderer={({ data: columnLabel }) => <>{columnLabel}</>}
                    noRowsRenderer={() => <div>No rows to show.</div>}
                    rowHeight={forecastGroupIndex =>
                      forecastGroups[forecastGroupIndex].forecasts.length * BASE_ROW_HEIGHT
                    }
                    filterBar={false}
                    alternateColors={false}
                  />
                </div>
                <div className="resizeContainer">
                  <div
                    className="resizeArea"
                    style={{ left: `${(leftTableWidth ?? 0) + 4}px` }}
                    onMouseDown={() => setResizeActive(true)}
                    onMouseMove={e => resizeFrame(e)}
                  >
                    <div className="tableDivider" />
                  </div>
                </div>
                <div
                  className="savedForecastsTableContainer rightTableContainer"
                  style={{ width: (doubleTableWidth ?? 0) - (leftTableWidth ?? 0) || "auto" }}
                >
                  <BPMTable
                    data={rightData}
                    headers={rightHeaders}
                    headersRenderer={({ data: columnLabel }) => <>{columnLabel}</>}
                    noRowsRenderer={() => <div>No rows to show.</div>}
                    rowHeight={BASE_ROW_HEIGHT}
                    filterBar={false}
                    alternateColors={false}
                  />
                </div>
              </>
            ) : (
              <FullPageSpinner />
            )}
          </div>
        }
      />
    </div>
  );
};

export default SavedForecasts;
